import React, { useMemo } from 'react';
import { scaleLinear } from 'd3-scale';

import Localization from '../../../Localization';
import { metrics, recursiveNest } from '../../Report';
import { geoKey, formatAs } from '../../../settings.js';
import { ChordGradients } from '../../../Gradients.js';

import './styles.scss';

const scales = {
  capacity: scaleLinear(),
  co2emissions: scaleLinear(),
};

function getLayers(geoType) {
  return {
    capacityByStatusLayers: [
      {
        key: d => geoKey[geoType](d),
        value: 'capacity',
        yOffset: metrics.labelHeight,
      },
      {
        key: d => d.status,
        value: 'capacity',
        yOffset: 0,
      },
      {
        key: d => d.technology,
        value: 'capacity',
        yOffset: 0,
      },
    ],
    capacityByTechnologyLayers: [
      {
        key: d => geoKey[geoType](d),
        value: 'capacity',
        yOffset: metrics.labelHeight,
      },
      {
        key: d => d.technology,
        value: 'capacity',
        yOffset: 0,
      },
      {
        key: d => d.status,
        value: 'capacity',
        yOffset: 0,
      },
    ],
    co2EmissionsByTechnologyLayers: [
      {
        key: d => d.technology,
        value: 'co2emissions',
        yOffset: metrics.labelHeight,
      },
      {
        key: d => geoKey[geoType](d),
        value: 'co2emissions',
        yOffset: 0,
      },
    ],
  };
}

function assignY(data, layers, index, scale) {
  const layer = layers[index];
  let y = 0;
  return data.map(d => {
    d.y = y;
    if (index === layers.length - 2) {
      let gy = 0;
      d.groups = d.groups.map(g => {
        g.height = scale(g.value);
        g.y = gy;
        gy += g.height;
        return g;
      });
    } else {
      d.groups = assignY(d.groups, layers, index + 1, scale);
    }
    d.height = d.groups.map(g => g.height).reduce((a, v) => a + v, 0);
    y += Math.max(layer.yOffset * 2, d.height) + layer.yOffset;
    return d;
  })
}

function arrangeColumn(data, layers, scale) {
  return assignY(recursiveNest(data, layers, 0), layers, 0, scale);
}

function generateChords(start, end, gradient, gradientsList) {
  return start.map(c0 => {
    const c1 = end.find(c => c.key === c0.key);
    if (c1 === undefined) {
      return null;
    }
    const x0 = 0,
          x1 = metrics.columnWidth - metrics.barWidth,
          y0 = c0.ay,
          y1 = c1.ay,
          h0 = c0.height,
          h1 = c1.height,
          midX = x0 + x1 * 0.5;
    let color = c0.color
    if (gradient) {
      color = `chordGradient-${c1.color}-${c0.color}`.replace(/#/g, '')
      gradientsList.push(color)
    }

    return {
      key: c0.key,
      color,
      path: `
        M ${x0} ${y0}
        C ${midX} ${y0} ${midX} ${y1} ${x1} ${y1}
        L ${x1} ${y1 + h1}
        C ${midX} ${y1 + h1} ${midX} ${y0 + h0} ${x0} ${y0 + h0}
        Z
      `,
    };
  }).filter(d => d);
}

export default function Sankey(props) {
  const { investments, geoType, yearExtent, setSankeyHeight, language } = props;

  const [yearMin, yearMax] = yearExtent;
  const columnHeadings = useMemo(() => [
    [
      (<text>{Localization.get('Capacity (MW)')}</text>),
      (<text>{Localization.get('by Status')}</text>),
      (<text>{`${yearMin}-${yearMax}`}</text>),
    ],
    [
      (<text>{Localization.get('Capacity (MW)')}</text>),
      (<text>{Localization.get('by Technology')}</text>),
    ],
    [
      (
        <text>
          <tspan>CO</tspan>
          <tspan dy={metrics.font.medium / 3} fontSize={metrics.font.medium}>2</tspan>
          <tspan dy={-metrics.font.medium / 3}>{` ${Localization.get('Emissions')}`}</tspan>
        </text>
      ),
      (<text>({Localization.get('K Ton')})</text>),
      (<text>{Localization.get('by Technology')}</text>),
    ],
  ], [yearMin, yearMax, language]); // eslint-disable-line react-hooks/exhaustive-deps

  const { capacityByStatusLayers, capacityByTechnologyLayers, co2EmissionsByTechnologyLayers } = useMemo(() => (
    getLayers(geoType)
  ), [geoType]);

  // Nest Capacity by Status part 1
  let capacityByStatus = useMemo(() => (
    recursiveNest(investments, capacityByStatusLayers, 0)
  ), [investments, capacityByStatusLayers]);

  // Nest Emissions by Technology part 1
  let co2EmissionsByTechnology = useMemo(() => (
    recursiveNest(investments, co2EmissionsByTechnologyLayers, 0)
  ), [investments, co2EmissionsByTechnologyLayers]);

  // Setup Scales
  const min = useMemo(() => ({
    capacity: Math.min(
      ...capacityByStatus.map(d => (
        d.groups.map(g => g.groups.map(p => p.value)).reduce((a, v) => a.concat(v), [])
      )).reduce((a, v) => a.concat(v), [])
    ),
    co2emissions: Math.min(
      ...co2EmissionsByTechnology.map(d => d.groups.map(g => g.value))
      .reduce((a, v) => a.concat(v), [])
    ),
  }), [capacityByStatus, co2EmissionsByTechnology]);
  const max = useMemo(() => ({
    capacity: Math.max(...capacityByStatus.map(d => d.value)),
    co2emissions: Math.max(...co2EmissionsByTechnology.map(d => d.value)),
  }), [capacityByStatus, co2EmissionsByTechnology]);
  scales.capacity.domain([min.capacity, max.capacity]).range([metrics.barWidth, metrics.sectionHeight]);
  scales.co2emissions.domain([min.co2emissions, max.co2emissions]).range([metrics.barWidth, metrics.sectionHeight]);
  const totalEmissions = co2EmissionsByTechnology.map(d => d.value).reduce((a, v) => a + v, 0);

  // Assign Y Positions
  capacityByStatus = useMemo(() => (
    assignY(capacityByStatus, capacityByStatusLayers, 0, scales.capacity)
  ), [capacityByStatus, capacityByStatusLayers]);
  co2EmissionsByTechnology = useMemo(() => (
    assignY(co2EmissionsByTechnology, co2EmissionsByTechnologyLayers, 0, scales.co2emissions)
  ), [co2EmissionsByTechnology, co2EmissionsByTechnologyLayers]);
  const sankeyInnerHeight = Math.max(...capacityByStatus.map(c => c.y + c.height));

  // Nest & Assign Y Positions Capacity by Technology
  const capacityByTechnology = useMemo(() => (
    arrangeColumn(investments, capacityByTechnologyLayers, scales.capacity)
  ), [investments, capacityByTechnologyLayers]);

  // Chords
  // TODO: Generalize these into functions? main difference is the nesting order (gotta make keys match)
  // left0 => left1
  const left0 = useMemo(() => (
    capacityByStatus.map(geo => (
      geo.groups.map(status => status.groups.map(tech => {
        tech.key = `${geo.name}-${status.name}-${tech.name}`;
        tech.ay = geo.y + status.y + tech.y;
        return tech;
      })).reduce((a, v) => a.concat(v), [])
    )).reduce((a, v) => a.concat(v), [])
  ), [capacityByStatus]);
  const left1 = useMemo(() => (
    capacityByTechnology.map(geo => (
      geo.groups.map(tech => tech.groups.map(status => {
        status.key = `${geo.name}-${status.name}-${tech.name}`;
        status.ay = geo.y + tech.y + status.y;
        return status;
      })).reduce((a, v) => a.concat(v), [])
    )).reduce((a, v) => a.concat(v), [])
  ), [capacityByTechnology]);
  // right0 => right1
  const right0 = useMemo(() => (
    capacityByTechnology.map(geo => (
      geo.groups.map(tech => {
        tech.key = `${geo.name}-${tech.name}`;
        tech.ay = geo.y + tech.y;
        return tech;
      })
    )).reduce((a, v) => a.concat(v), [])
  ), [capacityByTechnology]);
  const right1 = useMemo(() => (
    co2EmissionsByTechnology.map(tech => (
      tech.groups.map(geo => {
        geo.key = `${geo.name}-${tech.name}`;
        geo.ay = tech.y + geo.y;
        return geo;
      })
    )).reduce((a, v) => a.concat(v), [])
  ), [co2EmissionsByTechnology]);

  const gradientsRequired = [];
  const leftChords = generateChords(left0, left1, true, gradientsRequired);
  const rightChords = generateChords(right0, right1, false, gradientsRequired);

  const sankeyHeight = metrics.headerHeight + sankeyInnerHeight + metrics.margin.small * 3;
  setSankeyHeight(sankeyHeight);

  return (
    <g id='sankey'>
      <ChordGradients gradientsList={gradientsRequired} />
      <rect width={metrics.width} height={sankeyHeight} fill='#262626' />
      <g key='heading'>
        {columnHeadings.map((c, i) => (
          <g
            key={`column-${i}`}
            transform={`translate(${metrics.margin.large + metrics.columnWidth * i}, ${metrics.margin.small})`}
            fontSize={metrics.font.sankeyTitle}
          >
            {c.map((l, j) => (
              <g
                key={`header-row-${j}`}
                transform={`translate(0, ${metrics.font.large * j})`}
              >
                {l}
              </g>
            ))}
          </g>
        ))}
      </g>
      <g
        key='leftChords'
        fillOpacity='0.35'
        transform={`translate(${metrics.margin.large + metrics.barWidth}, ${(metrics.headerHeight + metrics.margin.small * 2)})`}
      >
        {leftChords.map(c => (<path key={c.key} d={c.path} fill={`url(#${c.color})`} />))}
      </g>
      <g
        key='rightChords'
        fillOpacity='0.35'
        transform={`translate(${metrics.margin.large + metrics.barWidth + metrics.columnWidth}, ${(metrics.headerHeight + metrics.margin.small * 2)})`}
      >
        {rightChords.map(c => (<path key={c.key} d={c.path} fill={c.color} />))}
      </g>
      <g
        key='capacityByStatus'
        transform={`translate(${metrics.margin.large}, ${(metrics.headerHeight + metrics.margin.small * 2)})`}
      >
        {capacityByStatus.map((c, i) => (
          <g key={c.name} transform={`translate(0, ${c.y})`}>
            <g
              transform={ `translate(0, ${-metrics.padding})`}
              fontSize={metrics.font.medium}
              style={{ textTransform: 'uppercase' }}
            >
              <text
                textAnchor='end'
                dx={-metrics.barWidth}
              >
                {i + 1} | {Localization.get(c.name.toUpperCase())}
              </text>
              <text>
                {formatAs.number(c.value)} MW
              </text>
            </g>
            <g>
              {c.groups.map(s => {
                const value = (<tspan fill='white'>{formatAs.number(s.value)} MW</tspan>);
                const valueToRight = s.height <= metrics.font.small * 2;
                return (
                  <g key={s.name} transform={`translate(0, ${s.y})`}>
                    <rect width={metrics.barWidth} height={s.height} fill={s.color} />
                    <g
                      transform={`translate(0, ${s.height / 2})`}
                      fontSize={metrics.font.small}
                    >
                      <g
                        transform={`translate(${-metrics.barWidth * 0.5}, ${valueToRight ? metrics.font.small / 2 : 0})`}
                        textAnchor='end'
                      >
                        <text fill={s.color}>
                          {Localization.get(s.name)} {valueToRight ? value : null}
                        </text>
                        <text y={metrics.font.small}>{valueToRight ? null : value}</text>
                      </g>
                      <text transform={`translate(${metrics.barWidth * 1.5}, ${metrics.font.small / 2})`}>
                        {formatAs.percent(s.value / c.value)}
                      </text>
                    </g>
                  </g>
                );
              })}
            </g>
          </g>
        ))}
      </g>
      <g
        key='capacityByTechnology'
        transform={`translate(${metrics.margin.large + metrics.columnWidth}, ${(metrics.headerHeight + metrics.margin.small * 2)})`}
      >
        {capacityByTechnology.map(c => (
          <g key={c.name} transform={`translate(0, ${c.y})`}>
            {c.groups.map(t => {
              const value = <tspan fill='white'>{formatAs.number(t.value)} MW</tspan>;
              const valueToRight = t.height <= metrics.font.small * 2;
              return (
                <g key={t.name} transform={`translate(0, ${t.y})`}>
                  <rect width={metrics.barWidth} height={t.height} fill={t.color} />
                  <g
                    transform={`translate(0, ${t.height / 2})`}
                    fontSize={metrics.font.small}
                  >
                    <text
                      transform={`translate(${-metrics.barWidth * 0.5}, ${metrics.font.small / 2})`}
                      textAnchor='end'
                    >
                      {formatAs.percent(t.value / c.value)}
                    </text>
                    <g transform={`translate(${metrics.barWidth * 1.5}, ${valueToRight ? metrics.font.small / 2 : 0})`}>
                      <text fill={t.color}>
                        {Localization.get(t.name)} {valueToRight ? value : null}
                      </text>
                      <text y={metrics.font.small}>{valueToRight ? null : value}</text>
                    </g>
                  </g>
                </g>
              );
            })}
          </g>
        ))}
      </g>
      <g
        key='co2EmissionsByTechnology'
        transform={`translate(${metrics.margin.large + metrics.columnWidth * 2}, ${(metrics.headerHeight + metrics.margin.small * 2)})`}
      >
        {co2EmissionsByTechnology.map(t => (
          <g key={t.name} transform={`translate(0, ${t.y})`}>
            <rect width={metrics.barWidth} height={t.height} fill={t.color} />
            <text
              transform={ `translate(0, ${-metrics.padding})`}
              fontSize={metrics.font.medium}
            >
              {formatAs.number(t.value)} {Localization.get('K Ton')}
            </text>
            <g
              fill={t.color}
              transform={`translate(${metrics.barWidth * 2}, ${Math.max(t.height / 2, metrics.font.large)})`}
            >
              <text fontSize={metrics.font.xlarge}>{Localization.get(t.name)}</text>
              <text y={metrics.font.large} fontSize={metrics.font.large}>
                {formatAs.percent(t.value / totalEmissions)}
              </text>
            </g>
            {t.groups.map(g => (
              <g
                key={g.name}
                transform={`translate(0, ${g.y})`}
              >
                <line x2={metrics.barWidth} stroke='#262626' />
                <text
                  fontSize={metrics.font.small}
                  transform={`translate(${-metrics.barWidth / 2}, ${metrics.font.small / 2 + g.height / 2})`}
                  textAnchor='end'
                >
                  {Localization.get(g.name.toUpperCase())} {formatAs.percent(g.value / t.value)}
                </text>
              </g>
            ))}
          </g>
        ))}
      </g>
    </g>
  );
}
